import React, { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarBlank } from '@phosphor-icons/react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { code: '+91', country: 'IN' }, { code: '+1', country: 'US' },
  { code: '+44', country: 'GB' }, { code: '+971', country: 'AE' },
  { code: '+65', country: 'SG' }, { code: '+61', country: 'AU' },
  { code: '+49', country: 'DE' }, { code: '+33', country: 'FR' },
  { code: '+81', country: 'JP' }, { code: '+86', country: 'CN' },
];

export default function AddTorDialog({ open, onClose, type, currency, onSuccess }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [sinceDate, setSinceDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const resetForm = () => {
    setName(''); setEmail(''); setPhone(''); setAmount(''); setSinceDate(null); setCountryCode('+91');
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!email.trim() && !phone.trim()) { toast.error('Either email or phone number is required'); return; }
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) { toast.error('Valid amount is required'); return; }
    if (!sinceDate) { toast.error('Since date is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/tors`, {
        tor_type: type,
        name: name.trim(),
        email: email.trim(),
        phone: `${countryCode}${phone.trim()}`,
        amount: parseFloat(amount),
        currency,
        since: sinceDate ? sinceDate.toISOString() : null
      }, { withCredentials: true });
      toast.success(`${type === 'debtor' ? 'Debtor' : 'Creditor'} added!`);
      resetForm();
      onClose();
      onSuccess();
    } catch (err) {
      toast.error('Failed to add');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { resetForm(); onClose(); } }}>
      <DialogContent className={`rounded-md max-w-md ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`} data-testid="add-tor-dialog">
        <DialogHeader>
          <DialogTitle className={`font-outfit text-lg font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
            Add {type === 'debtor' ? 'Debtor' : 'Creditor'}
          </DialogTitle>
          <DialogDescription className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            {type === 'debtor' ? 'Person who owes money to you' : 'Person you owe money to'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Name *</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Full name"
              className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="add-tor-name"
            />
          </div>

          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Email (or Phone) *</label>
            <Input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="email@example.com"
              type="email"
              className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="add-tor-email"
            />
          </div>

          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Phone (or Email) *</label>
            <div className="flex gap-2">
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger 
                  className={`w-24 h-10 rounded-sm font-ibm text-xs ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
                  data-testid="add-tor-country-code"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
                  {COUNTRY_CODES.map(cc => (
                    <SelectItem key={cc.code} value={cc.code} className="font-ibm text-xs">
                      {cc.code} {cc.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                placeholder="Phone number"
                className={`flex-1 h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
                data-testid="add-tor-phone"
              />
            </div>
          </div>

          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Amount ({currency}) *</label>
            <Input
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              type="number"
              min="0"
              step="0.01"
              className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="add-tor-amount"
            />
          </div>

<div>
  <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Since *</label>
  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
    <PopoverTrigger asChild>
      <button
        className={`w-full h-10 px-3 flex items-center gap-2 rounded-sm border font-ibm text-sm text-left ${
          isDark 
            ? 'bg-zinc-800 border-zinc-700 text-zinc-50 hover:bg-zinc-750' 
            : 'bg-zinc-50 border-zinc-200 text-zinc-950 hover:bg-zinc-100'
        } ${!sinceDate ? (isDark ? 'text-zinc-500' : 'text-zinc-400') : ''}`}
        data-testid="add-tor-since-trigger"
      >
        <CalendarBlank size={16} />
        {/* FIX: Ensure sinceDate is a valid Date object before formatting */}
        {sinceDate instanceof Date && !isNaN(sinceDate) 
          ? format(sinceDate, 'PPP') 
          : 'Select date'}
      </button>
    </PopoverTrigger>
    <PopoverContent className={`w-auto p-0 ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`} align="start">
      <Calendar
        mode="single"
        selected={sinceDate}
        onSelect={(d) => { 
          // FIX: Add a safeguard to ensure 'd' is valid
          if (d) {
            setSinceDate(d); 
            setCalendarOpen(false); 
          }
        }}
        disabled={(date) => date > new Date()}
        data-testid="add-tor-calendar"
        initialFocus
      />
    </PopoverContent>
  </Popover>
</div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={() => { resetForm(); onClose(); }}
            className={`flex-1 h-10 font-ibm text-sm font-medium rounded-sm border transition-all active:scale-[0.98] ${
              isDark ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
            }`}
            data-testid="cancel-add-tor"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 h-10 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 active:scale-[0.98] transition-all disabled:opacity-50"
            data-testid="submit-add-tor"
          >
            {saving ? 'Adding...' : 'Add'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}